import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'
import { HISTORICAL } from './CreateScenario.page'
import {
  CreateScenarioObjType,
  ProjectDetailsData,
  ScenariosData,
} from './Scenarios.types'
import { FormState } from './EditSettings/PortfolioSettings/SalesRate.types'

// v3 summary, not v1 /projects/{id}/details: same shape for what pages read
// (name, snapshot, scenario ids and names) from Table Storage alone. The v1
// route also queried Cosmos per scenario for counts no page shows.
const fetchProjectData = async (projectId: string | undefined) => {
  const { data } = await requestV3({
    url: `/projects/${projectId}/summary`,
  })
  return data
}

// prejectId is either a string or undefined
export const useProjectData = (
  projectId: string | undefined
): UseQueryResult<ProjectDetailsData> => {
  return useQuery({
    queryKey: ['project-details', projectId],
    queryFn: () => fetchProjectData(projectId),
    select: (data) => data.data,
    enabled: !!projectId,
    gcTime: 5 * 60 * 1000, // 5 minutes - project details change infrequently
    staleTime: 2 * 60 * 1000, // 2 minutes - consider fresh for 2 minutes
  })
}

const fetchCreateScenarioConfig = async (
  projectId: string | undefined,
  salesRatePeriodType = HISTORICAL
) => {
  const { data } = await requestV3({
    url: `/project/${projectId}/scenario-config`,
    method: 'post',
    data: {
      salesRatePeriodType,
    },
  })
  return data
}

export const useCreateScenarioConfig = (
  projectId: string | undefined,
  salesRatePeriodType: string
) => {
  return useQuery({
    queryKey: ['create-scenario-config', projectId, salesRatePeriodType],
    queryFn: () => fetchCreateScenarioConfig(projectId, salesRatePeriodType),
    select: (data) => data.data,
    enabled: !!projectId,
    // A snapshot's demand table doesn't change, so neither does this date range.
    staleTime: 30 * 60 * 1000,
  })
}

const getScenarios = async (projectId: string | undefined) => {
  const { data } = await requestV3({
    url: `/project/${projectId}/scenarios`,
    method: 'get',
  })
  return data
}

export const useScenariosData = (
  projectId: string | undefined
): UseQueryResult<ScenariosData> => {
  return useQuery({
    queryKey: ['scenarios', projectId],
    queryFn: () => getScenarios(projectId!),
    select: (data) => data.data,
    gcTime: 2 * 60 * 1000, // 2 minutes - scenarios list changes moderately
    staleTime: 30 * 1000, // 30 seconds - consider fresh for 30 seconds
    enabled: !!projectId,
  })
}

const createScenario = async (createScenariObj: CreateScenarioObjType) => {
  const projectId = createScenariObj.projectId
  const { data: responseData } = await requestV3({
    url: `/project/${projectId}/scenarios`,
    method: 'post',
    data: createScenariObj,
  })
  return responseData
}

// A project's scenarios appear in two queries: the scenarios grid and the
// project details that feed the breadcrumbs and sidebar names. A change to a
// scenario has to refresh both, or one of them shows the old list or name.
const invalidateProjectScenarios = (
  queryClient: QueryClient,
  projectId: string
) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['scenarios', projectId] }),
    queryClient.invalidateQueries({ queryKey: ['project-details', projectId] }),
  ])

export const useCreateScenario = (
  onSuccess: () => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, CreateScenarioObjType>({
    mutationFn: createScenario,
    onSuccess: (_, variables) => {
      invalidateProjectScenarios(queryClient, variables.projectId)
      onSuccess()
    },
    onError: () => {
      onFailure()
    },
  })
}

type CloneScenarioParams = {
  projectId: string
  scenarioId: string
}

type DeleteScenarioParams = {
  projectId: string
  scenarioId: string
}

type RenameScenarioParams = {
  projectId: string
  scenarioId: string
  newName: string
}

const cloneScenario = async (queryParams: CloneScenarioParams) => {
  const { data } = await requestV3({
    url: `/scenarios/clone-scenario?projectId=${queryParams.projectId}&scenarioId=${queryParams.scenarioId}`,
    method: 'post',
  })
  return data
}

export const useCloneScenario = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, CloneScenarioParams>({
    mutationFn: cloneScenario,
    onSuccess: (_, variables) => {
      invalidateProjectScenarios(queryClient, variables.projectId)
      onSuccess()
    },
    onError: () => {
      onError()
    },
  })
}

const deleteScenario = async (queryParams: DeleteScenarioParams) => {
  const { data } = await requestV3({
    url: `/scenarios/delete-scenario?projectId=${queryParams.projectId}&scenarioId=${queryParams.scenarioId}`,
    method: 'delete',
  })
  return data
}

export const useDeleteScenario = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeleteScenarioParams>({
    mutationFn: deleteScenario,
    onSuccess: (_, variables) => {
      invalidateProjectScenarios(queryClient, variables.projectId)
      onSuccess()
    },
    onError: () => {
      onError()
    },
  })
}

const renameScenario = async (renameScenarioParams: RenameScenarioParams) => {
  const { data } = await requestV3({
    url: `/scenarios/rename-scenario`,
    method: 'patch',
    data: renameScenarioParams,
  })
  return data
}

export const useRenameScenario = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, RenameScenarioParams>({
    mutationFn: renameScenario,
    onSuccess: (_, variables) => {
      invalidateProjectScenarios(queryClient, variables.projectId)
      onSuccess()
    },
    onError: () => {
      onError()
    },
  })
}

// PUT
// RoutesV3._api.add_resource(
//   ScenariosController,
//   "/api/v3/project/<project_id>scenarios/<scenario_id>/sales-rate",
//   endpoint="scenarios/sales-rate",
// )

type UpdateSalesRateType = {
  projectId: string
  scenarioId: string
  data: FormState
}

const getSalesRate = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/project/${projectId}/scenarios/${scenarioId}/sales-rate`,
    method: 'get',
  })
  return data
}

export const useSalesRate = (
  projectId: string,
  scenarioId: string
): UseQueryResult<any> => {
  return useQuery({
    queryKey: ['salesRate', projectId, scenarioId],
    queryFn: () => getSalesRate(projectId, scenarioId),
    select: (data) => data.data,
  })
}

const updateSalesRate = async (updateParams: UpdateSalesRateType) => {
  const { data: responseData } = await requestV3({
    url: `/project/${updateParams.projectId}/scenarios/${updateParams.scenarioId}/sales-rate`,
    method: 'put',
    data: {
      ...updateParams.data,
    },
  })
  return responseData
}

export const useUpdateSalesRate = (
  onSuccess: () => void,
  // The API's reason, e.g. a window with no demand data in it.
  onFailure: (message?: string) => void
) => {
  const queryClient = useQueryClient()

  return useMutation<
    void,
    { response?: { data?: { error?: string } } },
    UpdateSalesRateType
  >({
    mutationFn: updateSalesRate,
    onSuccess: (_, variables) => {
      // A new sales rate recalculates every line: all three line-settings
      // pages show its results.
      // salesRate: the window is saved with the rates.
      for (const key of [
        'product-settings',
        'batch-settings',
        'productAllocation',
        'base-source-site-workcentre',
        'salesRate',
      ]) {
        queryClient.invalidateQueries({
          queryKey: [key, variables.projectId, variables.scenarioId],
        })
      }
      onSuccess()
    },
    onError: (error) => {
      onFailure(error?.response?.data?.error)
    },
  })
}

// Update project with snapshot information

interface UpdateProjectParams {
  projectId: string
  snapshotId: string
  projectUserId: string
  snapshotUserId: string
}
const updateProject = async (updateProjectParams: UpdateProjectParams) => {
  const { data } = await requestV3({
    url: `/projects`,
    method: 'put',
    data: {
      ...updateProjectParams,
    },
  })
  return data
}

interface UseImportSnapshotParams {
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

export const useUpdateProject = ({
  onSuccess,
  onError,
}: UseImportSnapshotParams) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateProject,
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['project-details'] }),
        queryClient.invalidateQueries({ queryKey: ['scenarios'] }),
      ])
      onSuccess(data.message)
    },
    onError: (error: any) => {
      if (error?.response?.data?.error) {
        onError(error.response.data.error)
      } else {
        onError('An unexpected error occurred')
      }
    },
  })
}
